import PhotoActions from '../common/PhotoActions';
import React, { useState } from 'react';
import { useClothingItems } from '../../hooks/useClothingItems';
import { useGetClosetClothesQuery } from '../../redux/apis/endpoints/closetApi';
import ClothingCardsGrid from '../clothing-cards-grid/ClothingCardsGrid';
import OutfitPlaceholder from '../common/OutfitPlaceholder';
import ImportOptionsModal from '../import-options-modal/ImportOptionsModal';
import ExploreMoreLine from '../common/ExploreMoreLine';

const ClothingItemsPanel = ({
  onCardClick,
  onDragStart,
  onDragEnd,
  panelHeight = 250,
  panelPosition = 'right',
  isItemSelectedForTryOn,
  canSelectForTryOn,
  disabled = false,
  // Bulk selection props
  isBulkSelectMode = false,
  selectedItems = [],
  onItemSelect,
  onAddToCloset,
  onBulkDelete,
  // Closet selection props
  selectedClosetId = null,
  // Bulk edit handler
  onBulkEditClick,
  hasClothingItems = true,
  // Opens the explore/community panel
  onExploreClick,
  // Clothing modal handler
  onOpenClothingModal,
  // New feature modal handlers
  onOpenEmailModal,
  onOpenExtensionModal,
  // URL import modal handler
  onOpenUrlImportModal,
  // Login handler for guests
  onLoginRequired,
  // Scroll to a specific clothing item
  scrollToItemId
}) => {

  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  // Get all clothing items (used when no specific closet selected)
  const { clothingItems: allClothingItems, isLoading: isLoadingAll, isUploading } = useClothingItems();

  // Get closet-specific clothing items
  const {
    data: closetData,
    isLoading: isLoadingCloset,
    isFetching: isFetchingCloset
  } = useGetClosetClothesQuery(selectedClosetId, {
    skip: !selectedClosetId
  });

  // Determine loading state - show loading when switching closets to prevent flash
  const isLoading = selectedClosetId
    ? (isLoadingCloset || isFetchingCloset)
    : isLoadingAll;

  // Use closet-specific items if a closet is selected and data is ready
  // Return empty array while loading closet data to prevent showing all items
  const clothingItems = selectedClosetId
    ? (closetData?.items || [])
    : allClothingItems;

  // Show loading state when fetching closet data
  if (isLoading) {
    return (
      <div className="clothing-items-panel-container">
        <ClothingCardsGrid
          clothingItems={[]}
          isLoading={true}
        />
      </div>
    );
  }

  // Show empty state only when not loading and no items
  if (clothingItems.length === 0) {
    return (
      <div className="clothing-items-panel-container">
        <div
          className={`empty-closet-state ${panelPosition === 'bottom' ? 'bottom-panel-empty' : ''}`}
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            minHeight: panelPosition === 'bottom' ? 'auto' : Math.min(300, panelHeight || 300),
            padding: panelPosition === 'bottom' ? '16px' : '24px',
            textAlign: 'center',
            gap: '8px',
            cursor: 'pointer',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            WebkitTouchCallout: 'none'
          }}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
            e.currentTarget.classList.add('drag-over');
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            e.currentTarget.classList.remove('drag-over');
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            e.currentTarget.classList.remove('drag-over');
            const files = e.dataTransfer.files;
            if (files.length > 0 && files[0].type.startsWith('image/')) {
              const isLoggedIn = !!localStorage.getItem('token');
              if (!isLoggedIn && onLoginRequired) {
                onLoginRequired();
              } else if (onOpenClothingModal) {
                onOpenClothingModal(null, files[0]);
              }
            }
          }}
          onClick={() => {
            const isLoggedIn = !!localStorage.getItem('token');
            if (!isLoggedIn && onLoginRequired) {
              onLoginRequired();
            } else if (onOpenClothingModal) {
              onOpenClothingModal();
            }
          }}
        >
          <OutfitPlaceholder size={panelPosition === 'bottom' ? 100 : 140} />
          <div className="placeholder-subtitle" style={{ fontSize: 13, color: '#aaa', marginTop: 4 }}>Drop or upload clothing</div>
          <PhotoActions onLoginRequired={onLoginRequired}
            onFile={file => onOpenClothingModal?.(null, file)}
            onImport={() => setIsImportModalOpen(true)} />
        </div>

        {onExploreClick && <ExploreMoreLine onClick={onExploreClick} label="or explore the community" />}

        <ImportOptionsModal
          open={isImportModalOpen}
          onClose={() => setIsImportModalOpen(false)}
          onBrowseCommunity={onExploreClick}
          onOpenExplore={() => {
            const sheyouBase = window.location.hostname === 'localhost'
              ? (process.env.REACT_APP_sheyou_baseUrl_localTest || 'http://localhost:3000')
              : (process.env.REACT_APP_sheyou_baseUrl_live || 'https://sheyou.co');
            if (window.parent !== window) {
              window.parent.postMessage({ type: 'SUSU_NAVIGATE', path: '/browse' }, sheyouBase);
            } else {
              window.open(`${sheyouBase}/browse`, '_blank');
            }
          }}
          onOpenEmailModal={onOpenEmailModal}
          onOpenExtensionModal={onOpenExtensionModal}
          onOpenUrlImportModal={onOpenUrlImportModal}
        />
      </div>
    );
  }

  return (
    <div className="clothing-items-panel-container" style={{ position: 'relative' }}>
      {isUploading && (
        <div style={{
          position: 'absolute',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(255,255,255,0.7)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 50,
          gap: 12
        }}>
          <div style={{
            width: 36, height: 36,
            border: '3px solid #e0e0e0',
            borderTopColor: '#00b5ad',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite'
          }} />
          <span style={{ color: '#00b5ad', fontSize: 14, fontWeight: 500 }}>Uploading clothing item…</span>
        </div>
      )}

      <ClothingCardsGrid
        clothingItems={clothingItems}
        onCardClick={onCardClick}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        isItemSelectedForTryOn={isItemSelectedForTryOn}
        canSelectForTryOn={canSelectForTryOn}
        disabled={disabled}
        isBulkSelectMode={isBulkSelectMode}
        selectedItems={selectedItems}
        onItemSelect={onItemSelect}
        onAddToCloset={onAddToCloset}
        onBulkDelete={onBulkDelete}
        panelPosition={panelPosition}
        onBulkEditClick={onBulkEditClick}
        hasClothingItems={hasClothingItems}
        onOpenClothingModal={onOpenClothingModal}
        onOpenEmailModal={onOpenEmailModal}
        onOpenExtensionModal={onOpenExtensionModal}
        onOpenUrlImportModal={onOpenUrlImportModal}
        scrollToItemId={scrollToItemId}
      />

      {onExploreClick && <ExploreMoreLine onClick={onExploreClick} />}
    </div>
  );
};

export default ClothingItemsPanel;